/**
 * Unità di vendita ammesse dal gestionale.
 *
 * Copia di `crmstart-api/src/config/catalog.js`, stesso nome e stesso
 * contenuto: è lo schema già usato per `features.js`. Se cambia una, cambia
 * l'altra — lato API un test le confronta con l'ENUM del modello.
 *
 * Le spiegazioni non sono decorazione: «UD» e «CONF» non dicono niente a chi
 * non li ha mai visti, e questa domanda la legge un cliente al primo accesso.
 */
export const SALES_UNITS = Object.freeze([
  Object.freeze({
    value: "M2",
    label: "Metro quadro",
    hint: "Piastrelle, parquet, lastre: si vende la superficie.",
  }),
  Object.freeze({
    value: "ML",
    label: "Metro lineare",
    hint: "Battiscopa, profili, magatelli: si vende la lunghezza.",
  }),
  Object.freeze({
    value: "PZ",
    label: "Pezzo",
    hint: "Sanitari, rubinetteria, accessori: si contano a uno a uno.",
  }),
  Object.freeze({
    value: "UD",
    label: "Unità",
    hint: "Articoli che si contano ma non sono pezzi finiti, come i profili angolari.",
  }),
  Object.freeze({
    value: "CONF",
    label: "Confezione",
    hint: "Mosaici, set: si vende la confezione, non il contenuto.",
  }),
  Object.freeze({
    value: "SC",
    label: "Scatola",
    hint: "Si fattura la scatola intera, anche se il cliente ne chiede meno.",
  }),
  Object.freeze({
    value: "KG",
    label: "Peso",
    hint: "Materiali sfusi venduti a chilogrammo.",
  }),
  Object.freeze({
    value: "PAL",
    label: "Pallet",
    hint: "Forniture su bancale.",
  }),
]);

/** I soli codici, nell'ordine dell'elenco. */
export const SALES_UNIT_VALUES = Object.freeze(SALES_UNITS.map((u) => u.value));

/**
 * Le sole unità scelte dal cliente in configurazione, nell'ordine del catalogo.
 *
 * Se non ne ha scelta nessuna — istanza mai configurata — le mostra tutte:
 * una tendina vuota renderebbe la scheda articolo inutilizzabile, e l'unità è
 * ormai obbligatoria lato API.
 */
export function salesUnitsFor(chosen) {
  if (!Array.isArray(chosen) || chosen.length === 0) return SALES_UNITS;
  return SALES_UNITS.filter((u) => chosen.includes(u.value));
}

/**
 * Che cosa vende il cliente: le schede merceologiche del gestionale.
 *
 * [sc-48583 / task 48598] Copia di `crmstart-api/src/config/catalog.js`, come
 * già per le unità di vendita: le due repo devono dire la stessa cosa, e la
 * fonte di verità resta l'ENUM `items.category` del modello, che lato API un
 * test confronta con questo elenco.
 *
 * Il cliente sceglie le macro («Piastrelle», «Arredo bagno»); il database
 * salva le merceologie che ciascuna raccoglie. `categories` è il ponte fra le
 * due cose, ed è quello che permette alla scheda articolo di sapere quali
 * valori può usare.
 *
 * `bagno` sta fra le voci di «Arredo bagno» pur non essendo una merceologia:
 * è un valore vecchio, rimasto negli articoli caricati prima che l'ENUM si
 * dividesse. Escluderlo renderebbe non modificabili quegli articoli.
 */
export const PRODUCT_CATEGORIES = Object.freeze([
  Object.freeze({
    value: "piastrelle",
    label: "Piastrelle",
    hint: "Pavimenti e rivestimenti ceramici, a terra e a parete.",
    categories: Object.freeze(["piastrelle", "rivestimenti parete"]),
  }),
  Object.freeze({
    value: "legno",
    label: "Parquet e laminati",
    hint: "Pavimenti in legno, laminati e decking per esterno.",
    categories: Object.freeze(["parquet", "laminati", "decking esterno"]),
  }),
  Object.freeze({
    value: "bagno",
    label: "Arredo bagno",
    hint: "Sanitari, rubinetteria, mobili, lavabi, box doccia e accessori.",
    categories: Object.freeze([
      "sanitari",
      "rubinetteria",
      "box doccia",
      "accessori bagno",
      "mobili",
      "sistemi di scarico",
      "bagno",
    ]),
  }),
  Object.freeze({
    value: "profili",
    label: "Profili e battiscopa",
    hint: "Profili, giunzioni e battiscopa: si vendono a metro lineare.",
    categories: Object.freeze(["profili"]),
  }),
  Object.freeze({
    value: "infissi",
    label: "Porte e infissi",
    hint: "Porte interne e serramenti.",
    categories: Object.freeze(["porte e infissi"]),
  }),
  Object.freeze({
    value: "altro",
    label: "Altro",
    hint: "Quello che non rientra nelle altre schede.",
    categories: Object.freeze(["altro"]),
  }),
]);

/** Le sole macro, nell'ordine dell'elenco. */
export const PRODUCT_CATEGORY_VALUES = Object.freeze(
  PRODUCT_CATEGORIES.map((c) => c.value)
);

/** Tutti i valori di `items.category`, nell'ordine delle macro. */
export const ITEM_CATEGORY_VALUES = Object.freeze(
  PRODUCT_CATEGORIES.flatMap((c) => [...c.categories])
);

/**
 * Le sole macro scelte dal cliente, nell'ordine del catalogo.
 *
 * Gemella di `salesUnitsFor`, e con lo stesso ripiego: un'azienda che non ha
 * ancora risposto — o che ha configurato prima della 48594 — le vede tutte,
 * perché un elenco vuoto qui vorrebbe dire un gestionale senza schede.
 */
export function productCategoriesFor(chosen) {
  if (!Array.isArray(chosen) || chosen.length === 0) return PRODUCT_CATEGORIES;
  return PRODUCT_CATEGORIES.filter((c) => chosen.includes(c.value));
}

/**
 * I valori di `items.category` ammessi da un elenco di macro.
 *
 * Le macro che non esistono vengono ignorate invece di far cadere tutto.
 */
export function itemCategoriesFor(scelte) {
  if (!Array.isArray(scelte)) return [];
  const macro = PRODUCT_CATEGORIES.filter((c) => scelte.includes(c.value));
  return [...new Set(macro.flatMap((c) => [...c.categories]))];
}

/**
 * [sc-48351] Le categorie per cui si può filtrare il Magazzino.
 *
 * Le stesse che la creazione di un articolo propone - quelle delle schede
 * scelte in configurazione - più quelle che hanno già articoli, come fa la
 * pagina dei Listini. Prima il Magazzino aveva un elenco scritto a mano, fermo
 * a prima della 48598: «rubinetteria» o «sanitari» non si potevano filtrare.
 *
 * Chi non ha ancora scelto le vede tutte. `bagno` è un valore vecchio (vedi
 * sopra): compare solo se qualche articolo lo usa ancora.
 *
 * @param {string[]} [chosen] macro da `companies.productCategories`
 * @param {string[]} [inUse] valori di `items.category` con articoli attivi
 * @returns {string[]} valori di `items.category`, nell'ordine del catalogo
 */
export function warehouseCategoriesFor(chosen, inUse) {
  const usate = Array.isArray(inUse) ? inUse : [];
  const scelte =
    Array.isArray(chosen) && chosen.length > 0
      ? itemCategoriesFor(chosen)
      : ITEM_CATEGORY_VALUES;
  return ITEM_CATEGORY_VALUES.filter((categoria) =>
    categoria === "bagno"
      ? usate.includes(categoria)
      : scelte.includes(categoria) || usate.includes(categoria)
  );
}

/** La macro a cui appartiene un valore di `items.category`, o null. */
export function productCategoryOfItem(category) {
  const macro = PRODUCT_CATEGORIES.find((c) => c.categories.includes(category));
  return macro ? macro.value : null;
}

/** Nome per esteso di una macro: se non è nel catalogo torna il valore stesso. */
export function productCategoryLabel(value) {
  return (
    PRODUCT_CATEGORIES.find((c) => c.value === value)?.label ?? String(value)
  );
}

/**
 * Unità plausibili per ciascuna macro, da spuntare in partenza.
 *
 * [sc-48583 / task 48600] Il passo «Come vendi» partiva a caselle vuote, e
 * chi ha appena detto che vende piastrelle deve rispondere una seconda volta a
 * una domanda di cui si conosce già la risposta più probabile. Qui la si
 * propone; resta una proposta, e si deseleziona.
 *
 * NON è una regola, ed è la ragione per cui questa mappa vive solo lato web e
 * l'API non la conosce: l'unità è un dato dell'articolo, non della sua
 * categoria. Il progetto ci ha già sbattuto una volta — un piatto doccia
 * venduto a metro quadro ma classificato «bagno» finiva fra i pezzi e perdeva
 * la quantità — e da lì `groupOfItem` guarda `salesUnit` e non la merceologia.
 * Chi vende ceramica sfusa a chilogrammo deve poterlo dire lo stesso.
 */
const SUGGESTED_UNITS = Object.freeze({
  // La scatola c'è perché si fattura intera anche quando il cliente chiede
  // meno metri; il metro lineare per listelli e battiscopa.
  piastrelle: Object.freeze(["M2", "ML", "SC"]),
  legno: Object.freeze(["M2", "ML", "SC"]),
  bagno: Object.freeze(["PZ", "CONF"]),
  profili: Object.freeze(["ML", "UD"]),
  infissi: Object.freeze(["PZ"]),
  altro: Object.freeze(["PZ"]),
});

/**
 * Le unità da spuntare in partenza, viste le categorie scelte.
 *
 * @param {string[]} scelte macro scelte in configurazione
 * @returns {string[]} unità nell'ordine del catalogo, senza ripetizioni
 */
export function suggestedSalesUnits(scelte) {
  if (!Array.isArray(scelte)) return [];
  const suggerite = new Set();
  for (const macro of scelte) {
    for (const unit of SUGGESTED_UNITS[macro] || []) suggerite.add(unit);
  }
  // Ordine del catalogo, come `normalizeSalesUnits` lato API: le caselle si
  // presentano sempre nella stessa sequenza.
  return SALES_UNIT_VALUES.filter((unit) => suggerite.has(unit));
}

export default SALES_UNITS;
